import {
  AnonymizeCandidateModal,
  type AnonymizeCandidate,
} from './AnonymizeCandidateModal';
import { ExtendConsentModal } from './ExtendConsentModal';
import { RequestConsentModal } from './RequestConsentModal';
import {
  EXTENDABLE_STATUSES,
  RENEWABLE_STATUSES,
  REQUEST_CONSENT_MODAL_BODY,
  REQUEST_EXTENSION_MODAL_BODY,
  consentActionLabel,
} from './consentStatus';
import type { ConsentTarget, DerivedConsentStatus } from './types';

// Anonymize confirm for a table-row / More-menu modal slot. Every surface uses the same
// records-table modal, which lists the jobs / projects / flows the erase will strip.
interface AnonymizeFlowProps {
  candidate: AnonymizeCandidate;
  onClose: () => void;
  onSuccess?: () => void;
}

export const AnonymizeConsentFlow = ({
  candidate,
  onClose,
  onSuccess,
}: AnonymizeFlowProps) => (
  <AnonymizeCandidateModal
    candidate={candidate}
    modalActions={{ isOpen: true, onClose }}
    onSuccess={onSuccess}
  />
);

interface ConsentFlowProps {
  target: ConsentTarget;
  /** Row status; null (no consent yet) reads as a fresh, first-time grant. */
  derived: DerivedConsentStatus | null;
  onClose: () => void;
  onSuccess?: () => void;
  invalidateKeys?: unknown[][];
}

// "Mark consent manually" (fresh) / "Extend consent manually" (active) open the
// validity modal from a table-row slot — same modal the drawer banner uses, so
// the two surfaces behave the same instead of the row firing straight off.
export const MarkOrExtendConsentFlow = ({
  target,
  derived,
  onClose,
  onSuccess,
  invalidateKeys,
}: ConsentFlowProps) => {
  const status = derived ?? 'not_collected';
  return (
    <ExtendConsentModal
      target={target}
      action={EXTENDABLE_STATUSES.includes(status) ? 'extend' : 'mark_granted'}
      title={consentActionLabel('mark_manually', status)}
      isOpen
      onClose={onClose}
      onSuccess={onSuccess}
      invalidateKeys={invalidateKeys}
    />
  );
};

// "Request consent" / "Request consent extension" confirm before emailing the
// candidate — mirrors the drawer banner's request modal.
export const RequestConsentFlow = ({
  target,
  derived,
  onClose,
  onSuccess,
  invalidateKeys,
}: ConsentFlowProps) => {
  const status = derived ?? 'not_collected';
  return (
    <RequestConsentModal
      target={target}
      title={consentActionLabel('request', status)}
      description={
        RENEWABLE_STATUSES.includes(status)
          ? REQUEST_EXTENSION_MODAL_BODY
          : REQUEST_CONSENT_MODAL_BODY
      }
      isOpen
      onClose={onClose}
      onSuccess={onSuccess}
      invalidateKeys={invalidateKeys}
    />
  );
};
